import { useEffect, useState } from "react";
import { Box, CircularProgress, Dialog, IconButton, Tooltip, Typography } from "@mui/material";
import CloseIcon from '@mui/icons-material/Close';
import DownloadIcon from '@mui/icons-material/Download';
import { ChatAnexo, tamanhoLegivel } from "./chatTypes";
import { carregarImagemAnexo, urlImagemCache } from "./imagemAnexo";

type Props = {
    anexo: ChatAnexo | null
    onFechar: () => void
    onBaixar: (anexo: ChatAnexo) => void
};

// Visualizador em tela cheia. zIndex acima do Drawer do chat (3500 no ChatWidget).
export default function ModalImagemChat({ anexo, onFechar, onBaixar }: Props) {
    const [url, setUrl] = useState<string | null>(null);
    const [erro, setErro] = useState(false);

    useEffect(() => {
        if (!anexo) {
            setUrl(null);
            setErro(false);
            return;
        }

        // na prática já está em cache (veio da miniatura), mas o visualizador não depende disso
        setUrl(urlImagemCache(anexo.id_chat_anexo));
        setErro(false);

        let ativo = true;
        carregarImagemAnexo(anexo.id_chat_anexo, anexo.mime_type)
            .then(u => { if (ativo) setUrl(u); })
            .catch(() => { if (ativo) setErro(true); });
        return () => { ativo = false; };
    }, [anexo]);

    return (
        <Dialog
            open={Boolean(anexo)}
            onClose={onFechar}
            maxWidth={false}
            sx={{ zIndex: 3600 }}
            PaperProps={{
                sx: {
                    bgcolor: 'transparent',
                    boxShadow: 'none',
                    m: 1,
                    maxWidth: '96vw',
                    maxHeight: '94vh',
                    overflow: 'hidden',
                },
            }}
        >
            <Box sx={{ position: 'relative', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        alignSelf: 'stretch',
                        px: 1.5,
                        py: 0.8,
                        borderRadius: '8px 8px 0 0',
                        bgcolor: 'rgba(0,0,0,0.75)',
                    }}
                >
                    <Box sx={{ minWidth: 0, flexGrow: 1 }}>
                        <Typography sx={{ fontSize: 13, color: '#fff', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {anexo?.nome_original}
                        </Typography>
                        <Typography sx={{ fontSize: 10.5, color: '#bdbdbd' }}>
                            {anexo ? tamanhoLegivel(anexo.tamanho_bytes) : ''}
                        </Typography>
                    </Box>

                    <Tooltip title="Baixar imagem">
                        <IconButton size="small" sx={{ color: '#fff' }} onClick={() => anexo && onBaixar(anexo)}>
                            <DownloadIcon sx={{ fontSize: 20 }} />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Fechar">
                        <IconButton size="small" sx={{ color: '#fff' }} onClick={onFechar}>
                            <CloseIcon sx={{ fontSize: 20 }} />
                        </IconButton>
                    </Tooltip>
                </Box>

                <Box
                    sx={{
                        alignSelf: 'stretch',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        minHeight: 160,
                        borderRadius: '0 0 8px 8px',
                        bgcolor: 'rgba(0,0,0,0.9)',
                    }}
                >
                    {erro && (
                        <Typography sx={{ fontSize: 13, color: '#ef9a9a', p: 3 }}>
                            Não foi possível carregar a imagem.
                        </Typography>
                    )}
                    {!erro && !url && <CircularProgress size={28} sx={{ color: '#bdbdbd', m: 4 }} />}
                    {!erro && url && (
                        <img
                            src={url}
                            alt={anexo?.nome_original || ''}
                            onError={() => setErro(true)}
                            style={{ display: 'block', maxWidth: '94vw', maxHeight: '80vh', objectFit: 'contain' }}
                        />
                    )}
                </Box>
            </Box>
        </Dialog>
    );
}
